import React, { useEffect, useState } from 'react';
import { LucideIcon } from 'lucide-react';
import { PocToast } from '../services/pocRuntime';

export const INPUT_BASE_CLASS =
  "w-full h-12 px-4 text-sm text-gray-900 placeholder:text-gray-400 bg-white border border-primary/15 rounded-xl shadow-sm outline-none transition focus:ring-2 focus:ring-primary/30 focus:border-primary/30";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  children?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({ 
  children, 
  onClick, 
  variant = 'primary', 
  size = 'md',
  className = '', 
  icon: Icon,
  disabled = false,
  ...props
}) => {
  const baseStyle = "inline-flex items-center justify-center gap-2 rounded-2xl font-bold leading-none whitespace-nowrap transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2";
  
  const variants = {
    primary: "bg-primary text-white hover:bg-primary-dark shadow-lg shadow-primary/20",
    secondary: "bg-white text-primary hover:bg-gray-50 border border-primary/10 shadow-sm",
    outline: "border-2 border-primary text-primary hover:bg-primary/5",
    ghost: "bg-transparent text-primary hover:bg-primary/5"
  };

  const sizes = {
    sm: "h-10 px-4 text-sm",
    md: "h-12 px-5 text-base",
    lg: "h-14 px-6 text-lg",
  };

  const iconSizes = {
    sm: 16,
    md: 18,
    lg: 20,
  };

  const buttonType = props.type ?? "button";

  return (
    <button 
      type={buttonType}
      onClick={onClick} 
      disabled={disabled}
      className={`${baseStyle} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {Icon && <Icon size={iconSizes[size]} />}
      {children}
    </button>
  );
};

interface CardProps {
  children?: React.ReactNode;
  className?: string;
  onClick?: () => void;
  ariaLabel?: string;
}

export const Card: React.FC<CardProps> = ({
  children,
  className = "",
  onClick,
  ariaLabel,
}) => {
  if (!onClick) {
    return (
      <div className={`bg-white rounded-2xl shadow-sm border border-primary/10 overflow-hidden ${className}`}>
        {children}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={`w-full text-left bg-white rounded-2xl shadow-sm border border-primary/10 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 ${className}`}
    >
      {children}
    </button>
  );
};

interface BadgeProps {
  children?: React.ReactNode;
  color?: 'green' | 'red' | 'orange';
}

export const Badge: React.FC<BadgeProps> = ({ children, color = 'green' }) => {
  const styles = {
    green: "bg-green-100 text-green-800",
    red: "bg-red-100 text-red-800",
    orange: "bg-orange-100 text-orange-800"
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${styles[color]}`}>
      {children}
    </span>
  );
};

export const Price = ({ amount }: { amount: number }) => (
  <span className="font-bold">{amount.toFixed(2)}€</span>
);

export const ToastHost: React.FC = () => {
  const [toasts, setToasts] = useState<PocToast[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onToast = (evt: Event) => {
      const detail = (evt as CustomEvent<PocToast>).detail;
      if (!detail?.id || !detail?.message) return;

      setToasts((prev) => [...prev, detail].slice(-4));

      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== detail.id));
      }, 2600);
    };

    window.addEventListener("boba:toast", onToast as EventListener);
    return () => window.removeEventListener("boba:toast", onToast as EventListener);
  }, []);

  if (!toasts.length) return null;

  return (
    <div
      className="fixed right-4 bottom-4 z-[70] flex flex-col gap-2 pointer-events-none"
      role="region"
      aria-live="polite"
      aria-atomic="true"
      aria-label="Notifications"
    >
      {toasts.map((toast) => {
        const colors =
          toast.level === "success"
            ? "bg-green-700 text-white"
            : toast.level === "warning"
              ? "bg-amber-600 text-white"
              : "bg-gray-900 text-white";

        return (
          <div
            key={toast.id}
            role="status"
            className={`min-w-[220px] max-w-[320px] px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${colors}`}
          >
            {toast.message}
          </div>
        );
      })}
    </div>
  );
};
